import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  Benefit,
  ContactInfo,
  MappedResponseData,
  MaxRTEResMeta,
  MaxRteTransactionData,
  Plan,
  PolicyEligibility,
} from '../types'
import {
  MAX_POLICY_RANKS,
  MAX_POLICY_STATUS,
  MAX_RTE_CONSTANTS,
  MAX_RTE_PLANS_CONSTANTS,
  MAX_RTE_POLICY_RANK_PRIMARY_MAPPING,
  MAX_RTE_POLICY_RANK_SECONDARY_MAPPING,
} from '../constants'

export const getPolicyRank = (code: string): string => {
  if (!code) return ''
  if (
    code === MAX_POLICY_RANKS.PRIMARY ||
    MAX_RTE_POLICY_RANK_PRIMARY_MAPPING?.includes(code)
  ) {
    return MAX_POLICY_RANKS.PRIMARY
  }

  if (
    code === MAX_POLICY_RANKS.SECONDARY ||
    MAX_RTE_POLICY_RANK_SECONDARY_MAPPING?.includes(code)
  ) {
    return MAX_POLICY_RANKS.SECONDARY
  }

  return ''
}

export const getPolicyStatus = (code: string): string => {
  if (!code) return ''
  if (code === MAX_POLICY_STATUS.ELIGIBLE) {
    return MAX_POLICY_STATUS.Current
  }

  return ''
}

export const getFirstHealthBenefit = (data: Plan[]): Benefit | null => {
  for (const item of data) {
    const match = (item.benefits ?? []).find(
      (b) =>
        b.name === MAX_RTE_PLANS_CONSTANTS.HEALTH_BENEFIT_PLAN_COVERAGE &&
        b.status === MAX_RTE_PLANS_CONSTANTS.ACTIVE_COVERAGE &&
        b.statusCode === MAX_RTE_PLANS_CONSTANTS.ACTIVE_STATUS_CODE
    )
    if (match) return match
  }
  return null
}

export function getPharmacyWithContact(
  benefits: Benefit[]
): ContactInfo | null {
  const pharmacyBenefits = benefits.filter(
    (b) => b.name === MAX_RTE_PLANS_CONSTANTS.PHARMACY_NAME
  )

  for (const benefit of pharmacyBenefits) {
    if (benefit.contacts) {
      for (const contact of benefit.contacts) {
        const hasValidContact = contact.contactInformation?.find(
          (ci) => ci.name && ci.phone
        )
        if (hasValidContact) {
          return hasValidContact
        }
      }
    }
  }

  return null
}

const initialState: MaxRteTransactionData = {
  responseData: null,
  resMetaData: null,
  productModal: false,
  maxRTEAutotriggered: null,
  shouldAutoScroll: false,
}

const maxRteSlice = createSlice({
  name: 'maxRte',
  initialState,
  reducers: {
    maxRteDataMappingReducer: (
      state,
      action: PayloadAction<{
        response: PolicyEligibility[]
        resMeta: MaxRTEResMeta
      } | null>
    ) => {
      const responseData: MappedResponseData[] = []
      const payload = action.payload

      if (!payload) {
        state.responseData = []
        state.resMetaData = null
        return
      }

      payload.response.forEach((entry) => {
        const coverage = entry
        if (coverage) {
          responseData.push({
            id: coverage?.id,
            [MAX_RTE_CONSTANTS.KEYS.POLICY_STATUS]:
              getPolicyStatus(coverage?.policy_status ?? '') ?? '',
            [MAX_RTE_CONSTANTS.KEYS.POLICY_RANK]: getPolicyRank(
              coverage?.policy_rank_id ?? ''
            ),
            [MAX_RTE_CONSTANTS.KEYS.PAYER_NAME]: coverage?.payer_name ?? '',
            [MAX_RTE_CONSTANTS.KEYS.PLAN_NAME]: coverage?.plan_name ?? '',
            [MAX_RTE_CONSTANTS.KEYS.POLICY_ID]: coverage?.policy_id ?? '',
            [MAX_RTE_CONSTANTS.KEYS.GROUP_ID]: coverage?.group_id ?? '',
            [MAX_RTE_CONSTANTS.KEYS.EFFECTIVE_START_DATE]:
              coverage?.effective_start_date ?? '',
            [MAX_RTE_CONSTANTS.KEYS.EFFECTIVE_END_DATE]:
              coverage?.effective_end_date ?? '',
            [MAX_RTE_CONSTANTS.KEYS.PBM_NAME]: coverage?.pbm_name ?? '',
            [MAX_RTE_CONSTANTS.KEYS.PBM_PHONE]: coverage?.pbm_phone ?? '',
            [MAX_RTE_CONSTANTS.KEYS.POLICY_SOURCE]: coverage?.policy_source_id,
            [MAX_RTE_CONSTANTS.KEYS.ELIGIBILITY_END_DATE]:
              coverage?.eligibility_end_date,
            [MAX_RTE_CONSTANTS.KEYS.ELIGIBILITY_START_DATE]:
              coverage?.eligibility_start_date,
            [MAX_RTE_CONSTANTS.KEYS.ERRORS]: coverage?.errors,
            [MAX_RTE_CONSTANTS.KEYS.ERROR_MESSAGE]: coverage?.error_message,
            [MAX_RTE_CONSTANTS.KEYS.EXTERNAL_TRACE_ID]: coverage?.external_id,
            [MAX_RTE_CONSTANTS.KEYS.MAXTE_REQUEST_DATE_TIME]:
              coverage?.maxrte_request_date,
            [MAX_RTE_CONSTANTS.KEYS.PAYER_TYPE]: coverage?.payer_type,
            [MAX_RTE_CONSTANTS.KEYS.RX_BIN]: coverage?.rx_bin,
            [MAX_RTE_CONSTANTS.KEYS.RX_GROUP]: coverage?.rx_group,
            [MAX_RTE_CONSTANTS.KEYS.RX_PCN]: coverage?.rx_pcn,
            [MAX_RTE_CONSTANTS.KEYS.BENEFITS_LIST]: coverage?.benefits_list,
          } as MappedResponseData)
        }
      })

      state.resMetaData = {
        ...(payload.resMeta ?? {}),
      }
      state.responseData = responseData
    },
    resetMaxRteDataReducer: (state) => {
      state.responseData = null
      state.resMetaData = null
    },
    openProductModal: (
      state,
      action: PayloadAction<{
        modalState: boolean
      }>
    ) => {
      state.productModal = action?.payload?.modalState
    },
    updateMaxRTEShouldAutoScroll: (state, action: PayloadAction<boolean>) => {
      state.shouldAutoScroll = action.payload
    },
    updateMaxRTEAutotriggered: (
      state,
      action: PayloadAction<boolean | null>
    ) => {
      state.maxRTEAutotriggered = action?.payload
    },
  },
})

export const {
  maxRteDataMappingReducer,
  resetMaxRteDataReducer,
  openProductModal,
  updateMaxRTEAutotriggered,
  updateMaxRTEShouldAutoScroll,
} = maxRteSlice.actions
export default maxRteSlice.reducer
